/**
 * Platform-specific PWA install instructions.
 *
 * Returns numbered steps based on the user agent and display mode. The "already
 * installed" case stops needless instruction spam when the user has already
 * taken the action we would otherwise be telling them to take.
 */

import { isStandalone } from './pwaInstall.js'

/**
 * Detect platform and return install guide steps.
 *
 * The third argument exists for tests; the app passes nothing and the browser
 * globals are read.
 *
 * @returns {{
 *   platform: 'ios-safari' | 'ios-other' | 'android-chrome' | 'android-other' | 'desktop' | 'standalone',
 *   steps: Array<string>,
 *   hasNativePrompt: boolean
 * }}
 */
export function getInstallGuide(
  userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent,
  hasBeforeInstallPrompt = false,
  {
    standalone = isStandalone(),
    platform = typeof navigator === 'undefined' ? '' : navigator.platform,
    maxTouchPoints = typeof navigator === 'undefined' ? 0 : navigator.maxTouchPoints,
  } = {},
) {
  // Already installed — no further action needed
  if (standalone) {
    return {
      platform: 'standalone',
      steps: ['FragranceOS is already installed on this device.'],
      hasNativePrompt: false,
    }
  }

  const ua = userAgent || ''
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (platform === 'MacIntel' && (maxTouchPoints || 0) > 1)
  const isAndroid = /Android/.test(ua)

  // iOS Safari — manual Share → Add to Home Screen
  if (isIOS) {
    const isSafariShell = /Safari/.test(ua)
    const isOtherBrowserOrWebview = /CriOS|FxiOS|EdgiOS|OPiOS|FBAN|FBAV|Instagram|Line|Twitter|LinkedInApp/.test(ua)
    const isSafari = isSafariShell && !isOtherBrowserOrWebview

    if (isSafari) {
      return {
        platform: 'ios-safari',
        steps: [
          'Tap the Share button at the bottom of Safari',
          'Scroll down and tap "Add to Home Screen"',
          'Tap "Add" in the top-right corner',
        ],
        hasNativePrompt: false,
      }
    }

    // iOS non-Safari browsers cannot install PWAs
    return {
      platform: 'ios-other',
      steps: [
        'Open this page in Safari to install FragranceOS',
        'Tap and hold the link below, then choose "Open in Safari"',
      ],
      hasNativePrompt: false,
      copyLink: true, // the UI renders a real link to the app (long-pressable) plus a copy button
    }
  }

  // Android Chrome — native prompt or menu fallback
  if (isAndroid) {
    const isChrome = /Chrome/.test(ua) && !/EdgA|SamsungBrowser|OPR/.test(ua)

    if (isChrome) {
      // If we captured beforeinstallprompt, the UI will show a native button instead
      if (hasBeforeInstallPrompt) {
        return {
          platform: 'android-chrome',
          steps: ['Tap "Add to Home Screen" below'],
          hasNativePrompt: true,
        }
      }

      // Fallback when native prompt wasn't captured
      return {
        platform: 'android-chrome',
        steps: [
          'Tap the menu button (⋮) in the top-right corner',
          'Tap "Install app" or "Add to Home screen"',
        ],
        hasNativePrompt: false,
      }
    }

    // Android non-Chrome browsers
    return {
      platform: 'android-other',
      steps: [
        'Open this page in Chrome to install FragranceOS',
        'Tap and hold the link below, then choose "Open in Chrome"',
      ],
      hasNativePrompt: false,
      copyLink: true,
    }
  }

  // Desktop browsers
  return {
    platform: 'desktop',
    steps: [
      'Look for an install icon in your browser\'s address bar',
      'Click it and follow the prompts to install FragranceOS',
    ],
    hasNativePrompt: hasBeforeInstallPrompt,
  }
}
